import { useState } from "react"
import { useTranslation } from "react-i18next"

import type { ShopifySyncProgress } from "@/types/integrations"

/**
 * OKS-960 — The screen-reader announcer for a running import (UX §6).
 *
 * ## Milestones ONLY, never per page
 *
 * A 20-page import that announced every page would interrupt a screen reader 20
 * times, which is why the visible ticker is `aria-hidden` and this exists
 * separately. Three announcements, all terminal or first-of-kind:
 *
 *   started · first page landed · finished (with counts)
 *
 * Failure and truncation are deliberately absent: their own `Alert`s already
 * announce them (`role="alert"` for the failure, which IS worth interrupting,
 * and `role="status"` for truncation, which is not). Announcing them twice
 * would be the same defect this component exists to avoid.
 *
 * ## ⚠️ Silent in the snapshot state (AC19)
 *
 * When updates are not arriving the numbers are a page-load snapshot, so there
 * is no *event* to announce. Announcing a stale number is worse than announcing
 * nothing: it claims something just happened when nothing did, to exactly the
 * users who cannot see that the spinner is absent.
 *
 * Transitions are still TRACKED while not live, so reconnecting mid-import does
 * not replay milestones the viewer already missed.
 *
 * ## Why the state is adjusted during render rather than in an effect
 *
 * The announcement is a transition, not a derivable value — "started" and
 * "first page" both look identical in a single snapshot of the props. React's
 * documented pattern for that is comparing against previous state during
 * render; doing it in an effect would fire an extra commit for every page of a
 * 20-page import.
 */
export default function SyncAnnouncer({
  progress,
  live,
  lastProductCount,
  lastVariantCount,
}: {
  progress: ShopifySyncProgress | null
  live: boolean
  lastProductCount: number | null
  lastVariantCount: number | null
}) {
  const { t } = useTranslation()

  const [seen, setSeen] = useState({
    importing: false,
    landed: false,
    message: "",
    // ⚠️ "Were we following THIS run live?" — see the finished branch.
    followed: false,
  })

  const importing = progress !== null
  const landed = progress !== null && progress.page > 0

  if (importing !== seen.importing || landed !== seen.landed) {
    let message = seen.message
    let followed = seen.followed

    // ⚠️ THE FINISHED BRANCH MUST COME FIRST, and this ordering is the fix.
    //
    // On completion the server sets `sync_progress` to null — and the page
    // derives `live` from `sync_progress.started_by_viewer`, so `live`
    // collapses to false in the SAME render. With the `!live` guard first, it
    // won every time and the finished milestone was unreachable: dead code
    // that read as working, silently removing the feature's payoff from the
    // only people who cannot see the ticker being replaced by the last-sync
    // line.
    //
    // ⚠️ It is gated on `followed`, NOT on `live`, and that is what stops the
    // fix reopening AC19. A viewer watching someone else's import is in the
    // snapshot state and never announces anything — so `followed` stays false
    // and completion stays silent for them. Gating on `live` here would be
    // vacuous (it is false by then); dropping the gate entirely would announce
    // a stranger's import finishing, which Core Rule 11 forbids.
    if (!importing && seen.importing) {
      message = followed
        ? t("settings.brand.products.sync.progress.a11y_finished", {
            products: lastProductCount ?? 0,
            variants: lastVariantCount ?? 0,
          })
        : ""
      followed = false
    } else if (!live) {
      // Track the transition, say nothing. Also clears any previous message so
      // a reconnect cannot re-announce a stale one.
      message = ""
    } else if (importing && !seen.importing) {
      message = t("settings.brand.products.sync.progress.a11y_started")
      // We announced the start, so this run is ours to narrate to the end —
      // including if the socket drops before it completes. A true statement
      // about the viewer's own import beats silence.
      followed = true
    } else if (landed && !seen.landed) {
      message = t("settings.brand.products.sync.progress.a11y_first_page")
    }

    setSeen({ importing, landed, message, followed })
  }

  return (
    <span
      className="sr-only"
      role="status"
      /* Atomic so the whole sentence is read as one utterance rather than the
         changed fragment — these are short, complete statements. */
      aria-atomic="true"
      data-testid="brand-products-sync-announcer"
    >
      {seen.message}
    </span>
  )
}
